// Your personal meal feed: photo grid grouped by day. Only you see it.
import React, { useMemo, useState } from 'react';
import { Pressable, View, useWindowDimensions } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useHeader, useFormatDay } from '../hooks';
import { useNav } from '../navigation';
import { useProgress } from '../store/progress';
import { useRecipeLookup } from '../store/library';
import { deletePhoto } from '../platform/media';
import Photo from '../components/Photo';
import Sheet from '../components/Sheet';
import { confirm } from '../components/Dialogs';
import { Body, Button, EmptyState, Screen, SectionHeader } from '../components/ui';
import { spacing } from '../theme';
import type { MealLogEntry } from '../types';

export default function MealDiaryScreen() {
  const formatDay = useFormatDay();
  const { t } = useTranslation();
  const nav = useNav();
  const { log, removeMeals } = useProgress();
  const lookup = useRecipeLookup();
  const { width } = useWindowDimensions();
  const [open, setOpen] = useState<MealLogEntry | null>(null);
  useHeader({ title: t('diary.title') }, [t]);

  const days = useMemo(() => {
    const map = new Map<string, MealLogEntry[]>();
    for (const e of log) map.set(e.day, [...(map.get(e.day) ?? []), e]);
    return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [log]);

  const columns = width >= 700 ? 5 : 3;
  const size = (Math.min(width, 1200) - spacing.l * 2 - spacing.s * (columns - 1)) / columns;

  if (log.length === 0) {
    return <EmptyState icon="camera" title={t('diary.title')} body={t('diary.empty')} action={<Button label={t('path.logMeal')} icon="camera" onPress={() => nav.navigate('LogMeal', {})} />} />;
  }

  return (
    <Screen>
      {days.map(([day, entries]) => (
        <View key={day} style={{ gap: spacing.s }}>
          <SectionHeader title={formatDay(day, { weekday: 'long', day: 'numeric', month: 'long' })} />
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.s }}>
            {entries.map(e => (
              <Pressable key={e.id} accessibilityRole="imagebutton" accessibilityLabel={e.caption ?? lookup(e.recipeId ?? '')?.title ?? day} onPress={() => setOpen(e)}>
                <Photo media={e.photo} seed={e.id} icon="meal" style={{ width: size, height: size }} />
              </Pressable>
            ))}
          </View>
        </View>
      ))}
      <Sheet visible={!!open} onClose={() => setOpen(null)} title={open ? formatDay(open.day) : ''}>
        {open ? (
          <>
            <Photo media={open.photo} seed={open.id} style={{ width: '100%', aspectRatio: 1 }} />
            {open.caption ? <Body>{open.caption}</Body> : null}
            {open.recipeId && lookup(open.recipeId) ? (
              <Button
                label={lookup(open.recipeId)!.title}
                icon="recipe"
                variant="secondary"
                onPress={() => {
                  const id = open.recipeId!;
                  setOpen(null);
                  nav.navigate('Recipe', { id });
                }}
              />
            ) : null}
            <Button
              label={t('diary.deleteMeal')}
              icon="trash"
              variant="danger"
              onPress={async () => {
                if (!(await confirm(t('diary.deleteMeal'), t('common.deleteConfirmBody')))) return;
                const removed = removeMeals([open.id]);
                removed.forEach(r => deletePhoto(r.photo));
                setOpen(null);
              }}
            />
          </>
        ) : null}
      </Sheet>
    </Screen>
  );
}
